import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/queryKeys";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import type { RealtimeChannel } from "@supabase/supabase-js";

export interface Notification {
  id: string;
  user_id: string;
  type: "booking" | "message" | "ai_suggestion" | "statec" | "alert" | "system";
  title: string;
  message: string;
  link: string | null;
  metadata: Record<string, unknown>;
  read: boolean;
  created_at: string;
  expires_at: string | null;
}

export const useNotifications = (limit = 50) => {
  return useQuery({
    queryKey: queryKeys.notifications(limit),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        // Hide internal AI suggestion notifications - they are superficial
        .neq("type", "ai_suggestion")
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data as Notification[];
    },
  });
};

export const useUnreadNotificationCount = () => {
  return useQuery({
    queryKey: queryKeys.notificationsUnreadCount,
    queryFn: async () => {
      const { count, error } = await supabase
        .from("notifications")
        .select("*", { count: "exact", head: true })
        .eq("read", false)
        // Exclude internal AI suggestion notifications from count
        .neq("type", "ai_suggestion");

      if (error) throw error;
      return count ?? 0;
    },
  });
};

export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("notifications")
        .update({ read: true })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications() });
      queryClient.invalidateQueries({ queryKey: queryKeys.notificationsUnreadCount });
    },
  });
};

export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("notifications")
        .update({ read: true })
        .eq("read", false);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications() });
      queryClient.invalidateQueries({ queryKey: queryKeys.notificationsUnreadCount });
    },
  });
};

/**
 * OPTIMIZATION (2026-01-27): Added user_id filter to reduce Disk IO.
 * Previously subscribed to ALL users' notifications globally.
 * Now filters to current user only, reducing WAL scanning overhead.
 */
export const useRealtimeNotifications = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const channelRef = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    // Get current user ID for filtering
    const setupChannel = async () => {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        console.warn('[Realtime] No authenticated user - skipping notification subscription');
        return null;
      }

      const channel = supabase
        .channel("notifications-realtime")
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "notifications",
            filter: `user_id=eq.${user.id}`, // Filter to current user only
          },
          (payload) => {
            const newNotification = payload.new as Notification;

            // Skip internal AI suggestion notifications - they are superficial
            if (newNotification.type === "ai_suggestion") {
              return;
            }

            // Update the notifications cache
            queryClient.setQueryData(
              queryKeys.notifications(),
              (old: Notification[] | undefined) =>
                old ? [newNotification, ...old] : [newNotification]
            );

            // Invalidate unread count
            queryClient.invalidateQueries({
              queryKey: queryKeys.notificationsUnreadCount,
            });

            // Show toast notification with action
            toast(newNotification.title, {
              description: newNotification.message,
              action: newNotification.link
                ? {
                    label: "View",
                    onClick: () => navigate(newNotification.link!),
                  }
                : undefined,
            });
          }
        )
        .subscribe();

      return channel;
    };

    let isMounted = true;

    setupChannel().then((channel) => {
      if (isMounted && channel) {
        channelRef.current = channel;
      } else if (channel) {
        // Cleanup if unmounted during setup
        supabase.removeChannel(channel);
      }
    }).catch((error) => {
      console.error('[Realtime] Failed to setup channel:', error);
    });

    return () => {
      isMounted = false;
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [queryClient, navigate]);
};
